import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, Truck, PackageCheck, Beaker, Clock, Printer,
  RefreshCw, ChevronDown, ChevronRight, Package, ExternalLink,
  FlaskConical, CheckCircle2, ShieldAlert, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { trackingLink } from "@/lib/carrierTracking";

/**
 * Feb 2026 — `/admin/kits/today` daily fulfilment digest.
 *
 * Five buckets over SCWT-dispatched posted kits (kit_method=mail_out
 * with any shipping_integration_status ≠ null). Each bucket lists
 * expandable rows with inline actions:
 *   • Mark posted today   (all buckets)
 *   • Mark received today (all buckets)
 *   • Print label         (only where outbound_label_url is set)
 *   • Retry               (only in needs_action + error/manual state)
 *
 * Auto-refreshes every 60s (toggle). Test-mode banner shows when
 * Starshipit is in dry-run.
 */

const BUCKET_META = {
  needs_action: {
    label: "Needs action",
    tone: "rose",
    icon: AlertTriangle,
    testid: "kits-today-bucket-needs-action",
  },
  overdue: {
    label: "Overdue (>48h)",
    tone: "amber",
    icon: Clock,
    testid: "kits-today-bucket-overdue",
  },
  ready_to_pack: {
    label: "Ready to pack",
    tone: "emerald",
    icon: PackageCheck,
    testid: "kits-today-bucket-ready-to-pack",
  },
  in_transit: {
    label: "In transit",
    tone: "sky",
    icon: Truck,
    testid: "kits-today-bucket-in-transit",
  },
  awaiting_return: {
    label: "Awaiting return",
    tone: "amber",
    icon: Truck,
    testid: "kits-today-bucket-awaiting-return",
  },
  posted_today: {
    label: "Posted today",
    tone: "emerald",
    icon: PackageCheck,
    testid: "kits-today-bucket-posted-today",
  },
  received_today: {
    label: "Received today",
    tone: "cyan",
    icon: Beaker,
    testid: "kits-today-bucket-received-today",
  },
};

const TONE_STYLES = {
  rose:    { pill: "bg-rose-100 text-rose-700",       ring: "ring-rose-200",       icon: "text-rose-500" },
  amber:   { pill: "bg-amber-100 text-amber-800",     ring: "ring-amber-200",      icon: "text-amber-500" },
  sky:     { pill: "bg-sky-100 text-sky-700",         ring: "ring-sky-200",        icon: "text-sky-500" },
  emerald: { pill: "bg-emerald-100 text-emerald-700", ring: "ring-emerald-200",    icon: "text-emerald-500" },
  cyan:    { pill: "bg-cyan-100 text-cyan-700",       ring: "ring-cyan-200",       icon: "text-cyan-500" },
};

// Order the tabs render in — top-priority buckets first. The new
// "Ready to pack" bucket sits immediately after "Needs action" so
// packing staff open the page and see their queue at a glance.
const BUCKET_ORDER = ["needs_action", "ready_to_pack", "overdue", "in_transit", "awaiting_return", "posted_today", "received_today"];

const AUDIT_ACTION_LABEL = {
  posted_kit_patch:              "Fields updated",
  posted_kit_fulfilment_required: "Fulfilment triggered",
  posted_kit_marked_posted:      "Marked posted today",
  posted_kit_marked_received:    "Marked received today",
  posted_kit_retry_requested:    "Retry requested",
};

function StatusPill({ status }) {
  if (!status) return <span className="text-xs text-slate-400">—</span>;
  const label = status.replace(/_/g, " ");
  const tone =
    status === "error" || status === "manual_action_required" ? "rose" :
    status === "awaiting_fulfilment" || status === "print_requested" ? "amber" :
    status === "kit_posted" || status === "delivered" ? "emerald" :
    status === "kit_packed" || status === "labels_generated" || status === "labels_printed" || status === "fulfilment_triggered" ? "sky" :
    "cyan";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${TONE_STYLES[tone].pill}`}>
      {label}
    </span>
  );
}

// ─── Two-label packing helpers ─────────────────────────────────────
const OK_LABEL_STATUSES = new Set([
  "label_created", "print_requested", "printed", "manifested",
  "dispatched", "in_transit", "out_for_delivery", "delivered",
]);
const FAIL_LABEL_STATUSES = new Set(["failed", "manual_action_required"]);

const PACKING_STATE_META = {
  ready:   { label: "Ready to pack",  tone: "emerald", icon: "✅" },
  partial: { label: "One label missing", tone: "amber",  icon: "⚠️" },
  failed:  { label: "Both failed",    tone: "rose",    icon: "✕"  },
  pending: { label: "Pending",        tone: "slate",   icon: "…"  },
};

function PackingStatePill({ state }) {
  const meta = PACKING_STATE_META[state] || PACKING_STATE_META.pending;
  const cls = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
  }[meta.tone];
  return (
    <span
      data-testid={`kits-today-packing-state-${state}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      <span aria-hidden>{meta.icon}</span> {meta.label}
    </span>
  );
}

function LabelBlock({ kind, block }) {
  const status = block?.label_status;
  const ok = OK_LABEL_STATUSES.has(status);
  const failed = FAIL_LABEL_STATUSES.has(status);
  const isOutbound = kind === "outbound";
  const title = isOutbound ? "Outbound Kit Label" : "Sample Return Express";
  const subtitle = isOutbound ? "Lab → Customer · sticks OUTSIDE the mailer"
                              : "Customer → Lab · ENCLOSED inside the kit";
  const tone = ok ? "border-emerald-200 bg-emerald-50/40"
             : failed ? "border-rose-200 bg-rose-50/40"
             : status ? "border-cyan-200 bg-cyan-50/40"
             : "border-slate-200 bg-slate-50/40";
  const tracking = block?.tracking_number;
  const track = trackingLink({
    trackingNumber: tracking, trackingUrl: block?.tracking_url, kind,
    carrier: block?.carrier, carrierName: block?.carrier_name,
    serviceCode: block?.service_code,
  });

  return (
    <div
      data-testid={`kits-today-label-${kind}`}
      className={`rounded-lg border p-3 ${tone}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">{title}</div>
          <div className="text-[10px] text-slate-500 leading-snug">{subtitle}</div>
        </div>
        <StatusPill status={status || "pending"} />
      </div>
      <div className="text-[11px] space-y-0.5">
        <div><span className="text-slate-500">Order ref:</span>{" "}
          <span className="font-mono text-slate-800" data-testid={`kits-today-label-${kind}-order-ref`}>
            {block?.order_reference || "—"}
          </span>
        </div>
        <div><span className="text-slate-500">Service:</span>{" "}
          <span className="font-mono text-slate-800" data-testid={`kits-today-label-${kind}-service-code`}>
            {block?.service_code || "—"}
          </span>
          {block?.service_code === "B20" && <span className="ml-1 text-slate-500">Express</span>}
          {block?.service_code === "B30" && <span className="ml-1 text-slate-500">Standard</span>}
        </div>
        <div><span className="text-slate-500">Tracking:</span>{" "}
          {track?.href ? (
            <a
              href={track.href}
              target="_blank" rel="noreferrer noopener"
              title={track.title}
              data-testid={`kits-today-label-${kind}-tracking-link`}
              className="font-mono text-cyan-700 hover:underline"
            >
              {tracking}
            </a>
          ) : tracking ? (
            <span className="font-mono text-slate-700"
              data-testid={`kits-today-label-${kind}-tracking-plain`}
              title={track?.title}>{tracking}</span>
          ) : <span className="text-slate-400">—</span>}
          {track?.carrierLabel && (
            <span className="ml-1 text-[10px] text-slate-500">{track.carrierLabel}</span>
          )}
        </div>
        <div><span className="text-slate-500">Print status:</span>{" "}
          <span className="font-mono text-slate-800">{block?.print_status || "—"}</span>
        </div>
        {block?.label_url && (
          <div>
            <a
              href={block.label_url}
              target="_blank" rel="noreferrer noopener"
              className="text-cyan-700 hover:underline text-[11px] font-semibold"
            >
              Download label PDF
            </a>
          </div>
        )}
        {block?.last_error && (
          <div className="mt-1 text-rose-800 text-[10px] leading-snug">
            <span className="font-semibold">Error:</span> {block.last_error}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Feb 2026 (SCWT-52B41C follow-up) — PWS Bulk Kit Order row.
 *
 * Renders inside the same Kits Today buckets (Needs Action, Ready to
 * Pack, Posted Today) as customer kits, but with:
 *   • Sky-blue "PWS bulk kit" type chip so staff can tell them apart
 *   • Partner name + box count + label counts
 *   • Deep-link "Open packing screen →" button (per-box workflow lives
 *     in AdminBulkKitPacking.jsx)
 *   • No inline mark-posted button — the packing screen owns the
 *     bulk mark-shipped action so per-box tracking numbers are set
 *     at the same time.
 */
function BulkKitRow({ row, onReload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const stageChip = {
    labels_to_create: { text: "Labels needed",   tone: "bg-amber-100 text-amber-900 border-amber-200" },
    ready_to_pack:    { text: "Ready to pack",   tone: "bg-sky-100 text-sky-900 border-sky-200" },
    sent:             { text: "Sent",            tone: "bg-emerald-100 text-emerald-900 border-emerald-200" },
    inactive:         { text: "Inactive",        tone: "bg-slate-100 text-slate-500 border-slate-200" },
  }[row.fulfilment_stage] || { text: row.fulfilment_stage || "—", tone: "bg-slate-100 text-slate-600 border-slate-200" };

  const canMarkShipped = row.fulfilment_stage === "ready_to_pack";
  const markShipped = async (e) => {
    e.stopPropagation();
    if (!confirm(
      `Mark bulk order ${row.order_number} as shipped?\n\n` +
      `${row.boxes} box${row.boxes === 1 ? "" : "es"} · ${row.kits} kit${row.kits === 1 ? "" : "s"}\n\n` +
      `This will send the partner dispatch email and increment their kit inventory.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/admin/bulk-kit-orders/${row.order_id}/mark-shipped`, {});
      toast.success(`Bulk order ${row.order_number} marked shipped.`);
      onReload?.({ silent: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`kits-today-bulk-row-${row.reference}`}
      className="border-t border-slate-100 first:border-t-0"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-4 py-3 hover:bg-sky-50/40 transition-colors flex items-center gap-3"
      >
        {open
          ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              data-testid="kits-today-row-type-chip"
              className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
            >
              <Package className="w-3 h-3" /> PWS bulk kit
            </span>
            <span className="font-mono text-xs font-semibold text-slate-900">{row.order_number || row.reference}</span>
            <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${stageChip.tone}`}>
              {stageChip.text}
            </span>
            <span className="text-[10px] text-slate-600 font-medium">
              {row.boxes} box{row.boxes === 1 ? "" : "es"} · {row.kits} kit{row.kits === 1 ? "" : "s"}
            </span>
          </div>
          <div className="text-xs text-slate-500 truncate mt-0.5">
            {row.customer_name || "(unknown partner)"}
            {row.customer_email ? ` — ${row.customer_email}` : ""}
          </div>
        </div>
      </button>
      {open && (
        <div className="bg-sky-50/30 border-t border-slate-100 px-4 py-3 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="font-semibold text-slate-700 mb-1">Outbound labels</div>
              <div className="text-slate-600">
                {row.outbound_labels_created} / {row.boxes} created
              </div>
            </div>
            <div>
              <div className="font-semibold text-slate-700 mb-1">Return labels (inside boxes)</div>
              <div className="text-slate-600">
                {row.return_labels_created} / {row.kits} created
              </div>
            </div>
            {row.shipped_at && (
              <div>
                <div className="font-semibold text-slate-700 mb-1">Shipped at</div>
                <div className="text-slate-600">{formatDateAU(row.shipped_at)}</div>
              </div>
            )}
            {row.created_at && (
              <div>
                <div className="font-semibold text-slate-700 mb-1">Ordered on</div>
                <div className="text-slate-600">{formatDateAU(row.created_at)}</div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 pt-1">
            {canMarkShipped && (
              <button
                type="button"
                data-testid={`kits-today-bulk-mark-shipped-${row.reference}`}
                disabled={busy}
                onClick={markShipped}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 px-2.5 py-1.5 rounded-md"
              >
                <Truck className="w-3.5 h-3.5" />
                {busy ? "Marking shipped…" : "Mark shipped"}
              </button>
            )}
            {row.action_url && (
              <Link
                to={row.action_url}
                data-testid={`kits-today-bulk-packing-link-${row.reference}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900 bg-white border border-sky-200 px-2.5 py-1.5 rounded-md hover:bg-sky-50"
              >
                <Package className="w-3.5 h-3.5" />
                Open packing screen →
              </Link>
            )}
            <Link
              to={`/admin/bulk-kit-orders?bucket=${row.fulfilment_stage}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 px-2.5 py-1.5 rounded-md hover:bg-slate-50"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open bulk orders
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}


function KitRow({ row, onMarkPosted, onMarkReceived, onPrint, onRetry, onReload, activeBucket }) {
  // Feb 2026 (SCWT-52B41C follow-up) — dispatch to the bulk-kit row
  // renderer when this is a PWS bulk kit order. Shares bucket layout
  // with customer kits so ops staff see one unified list.
  if (row.item_type === "pws_bulk_kit_order") {
    return <BulkKitRow row={row} onReload={onReload} />;
  }
  return (
    <CustomerKitRow
      row={row}
      onMarkPosted={onMarkPosted}
      onMarkReceived={onMarkReceived}
      onPrint={onPrint}
      onRetry={onRetry}
      activeBucket={activeBucket}
    />
  );
}

function CustomerKitRow({ row, onMarkPosted, onMarkReceived, onPrint, onRetry, activeBucket }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canRetry = activeBucket === "needs_action" &&
                    (row.shipping_integration_status === "error" ||
                     row.shipping_integration_status === "manual_action_required");
  const canPrint = !!row.outbound_label_url;
  const addr = row.posted_kit_address || {};
  const addrLine = [addr.line1, addr.suburb, addr.postcode].filter(Boolean).join(", ");

  const run = async (fn) => { setBusy(true); try { await fn(row.reference); } finally { setBusy(false); } };

  return (
    <div
      data-testid={`kits-today-row-${row.reference}`}
      className="border-t border-slate-100 first:border-t-0"
    >
      <button
        type="button"
        data-testid={`kits-today-row-toggle-${row.reference}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Type chip — distinguishes customer kit from PWS bulk */}
            <span
              data-testid="kits-today-row-type-chip"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
            >
              Customer kit
            </span>
            <span className="font-mono text-xs font-semibold text-slate-900">{row.reference}</span>
            <StatusPill status={row.shipping_integration_status} />
            {/* Aug 2026 fix — suppress the "Ready to pack" pill once the
                kit has actually been posted / is in transit / received.
                packing_state == "ready" refers to LABEL-CREATION readiness
                (both labels stamped), not physical packing state, so it
                stays "ready" forever after posting and creates a stale
                chip that contradicts the "kit posted" status pill beside
                it. We only show the pill in buckets where packing is a
                relevant concern OR when a label leg is partial/failed
                (which IS an alert even after posting). */}
            {(["needs_action", "ready_to_pack", "overdue"].includes(activeBucket)
              || row.packing_state === "partial"
              || row.packing_state === "failed"
            ) && <PackingStatePill state={row.packing_state || "pending"} />}
            {row.overdue_chase_email_sent_at && (
              <span
                data-testid={`kits-today-nudged-${row.reference}`}
                title={`Return-chase email sent ${String(row.overdue_chase_email_sent_at).slice(0, 10)}`}
                className="text-[10px] font-semibold text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded"
              >
                Nudged {String(row.overdue_chase_email_sent_at).slice(0, 10)}
              </span>
            )}
            {row.hours_since_updated != null && row.hours_since_updated >= 48 && (
              <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                {Math.floor(row.hours_since_updated)}h
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 truncate mt-0.5">
            {row.customer_name || "(no name)"} — {addrLine || "(no address)"}
          </div>
        </div>
      </button>
      {open && (
        <div className="bg-slate-50 border-t border-slate-100 px-4 py-3 space-y-3">
          {/* Two-label Starshipit summary (Feb 2026) */}
          <div data-testid={`kits-today-two-label-${row.reference}`} className="grid sm:grid-cols-2 gap-2">
            <LabelBlock kind="outbound"      block={row.outbound} />
            <LabelBlock kind="sample_return" block={row.sample_return} />
          </div>

          {/* Packing workflow reminder */}
          <div
            data-testid={`kits-today-packing-reminder-${row.reference}`}
            className="rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-[11px] text-cyan-900 leading-snug"
          >
            <div className="font-semibold mb-0.5">Packing workflow</div>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Stick the <b>Outbound Kit Label</b> on the OUTSIDE of the mailer.</li>
              <li>Place the <b>Sample Return Express Label</b> INSIDE the kit for the customer to send the sample back to the lab.</li>
            </ol>
            {row.packing_state && row.packing_state !== "ready" && (
              <div className="mt-1 text-amber-800 font-semibold">
                ⚠ Do not pack yet — both labels must be generated first. Use the Shipping panel on the booking page to create/retry.
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
            <div><span className="text-slate-500">Customer email:</span> <span className="font-medium">{row.customer_email || "—"}</span></div>
            <div><span className="text-slate-500">Provider:</span> <span className="font-medium">{row.shipping_provider || "—"}</span></div>
            <div><span className="text-slate-500">Last updated:</span> <span className="font-mono">{row.posted_kit_updated_at ? formatDateAU(row.posted_kit_updated_at, { withTime: true }) : "—"}</span></div>
            <div><span className="text-slate-500">Posted date:</span> <span className="font-mono">{row.date_kit_posted || "—"}</span></div>
            <div><span className="text-slate-500">Received date:</span> <span className="font-mono">{row.date_sample_received || "—"}</span></div>
          </div>
          {row.fulfilment_error_message && (
            <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
              <span className="font-semibold">Last error:</span> {row.fulfilment_error_message}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <button
              type="button"
              data-testid={`kits-today-action-mark-posted-${row.reference}`}
              onClick={() => run(onMarkPosted)}
              disabled={busy}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 transition-colors"
            >
              <PackageCheck className="w-3 h-3" /> Mark posted today
            </button>
            <button
              type="button"
              data-testid={`kits-today-action-mark-received-${row.reference}`}
              onClick={() => run(onMarkReceived)}
              disabled={busy}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-50 transition-colors"
            >
              <Beaker className="w-3 h-3" /> Mark received today
            </button>
            {canPrint && (
              <button
                type="button"
                data-testid={`kits-today-action-print-${row.reference}`}
                onClick={() => onPrint(row)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors"
              >
                <Printer className="w-3 h-3" /> Print label
              </button>
            )}
            {canRetry && (
              <button
                type="button"
                data-testid={`kits-today-action-retry-${row.reference}`}
                onClick={() => run(onRetry)}
                disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className="w-3 h-3" /> Retry
              </button>
            )}
            <Link
              to={`/admin/bookings/${row.reference}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold border border-slate-300 text-slate-700 hover:bg-white transition-colors"
            >
              <ExternalLink className="w-3 h-3" /> Open booking
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function BucketCard({ bucketKey, rows, activeBucket, setActiveBucket, actions }) {
  const meta = BUCKET_META[bucketKey];
  const tone = TONE_STYLES[meta.tone];
  const Icon = meta.icon;
  const isActive = activeBucket === bucketKey;
  return (
    <button
      type="button"
      data-testid={meta.testid}
      onClick={() => setActiveBucket(bucketKey)}
      className={`text-left rounded-2xl border transition-all p-4 ${isActive ? "bg-white border-slate-300 shadow-md ring-2 " + tone.ring : "bg-white/70 border-slate-200 hover:border-slate-300"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <Icon className={`w-5 h-5 ${tone.icon}`} />
        <span
          data-testid={`${meta.testid}-count`}
          className={`inline-flex items-center justify-center min-w-[1.6rem] h-6 px-2 rounded-full text-xs font-bold ${tone.pill}`}
        >
          {rows.length}
        </span>
      </div>
      <div className="mt-2 text-sm font-semibold text-slate-900">{meta.label}</div>
    </button>
  );
}

function ActivityFeed({ items }) {
  if (!items?.length) {
    return (
      <div data-testid="kits-today-activity-empty" className="text-xs text-slate-400 py-6 text-center">
        No fulfilment activity in the last 24 hours.
      </div>
    );
  }
  return (
    <ul data-testid="kits-today-activity-list" className="divide-y divide-slate-100">
      {items.map((e, idx) => (
        <li key={`${e.reference}-${e.at}-${idx}`} data-testid={`kits-today-activity-item-${idx}`} className="py-2.5 flex items-start gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-slate-900">
              {AUDIT_ACTION_LABEL[e.action] || e.action}
              <Link to={`/admin/bookings/${e.reference}`} className="ml-2 font-mono text-[11px] text-cyan-700 hover:underline">
                {e.reference}
              </Link>
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {e.customer || "(no name)"}{e.actor ? ` · by ${e.actor}` : ""}
            </div>
            <div className="text-[10px] text-slate-400">{formatDateAU(e.at, { withTime: true })}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function AdminKitsToday() {
  // Feb 2026 (SCWT-52B41C follow-up) — read `?bucket=<key>` from the
  // URL so the Fulfilment Outstanding badge popover can deep-link
  // straight into a bucket. Mapping from badge keys → page bucket
  // keys is one-way (the badge uses lifecycle-stage keys, the page
  // uses shipping-lifecycle keys) and lives right here so both sides
  // stay honest.
  const BADGE_KEY_TO_BUCKET = {
    labels_to_create: "needs_action",
    kit_to_post:      "ready_to_pack",
    awaiting_return:  "awaiting_return",
    // Overdue subset of awaiting_return → jump to the "overdue"
    // shipping bucket which shows any stalled awaiting_fulfilment
    // rows. The FE-side overdue-return callout is a separate future
    // enhancement; for now this lands you in a sensible place.
    awaiting_return_overdue: "overdue",
  };
  const [searchParams] = useSearchParams();
  const bucketFromUrl = searchParams.get("bucket");
  const initialBucket = (bucketFromUrl && BADGE_KEY_TO_BUCKET[bucketFromUrl])
    || bucketFromUrl
    || "needs_action";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeBucket, setActiveBucket] = useState(initialBucket);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const timerRef = useRef(null);

  // When the URL bucket param changes (deep-link from the badge on
  // an already-open tab), snap to it. Also runs on first mount as a
  // belt-and-braces guarantee that a `?bucket=…` deep-link always
  // wins over the default `needs_action`.
  useEffect(() => {
    if (!bucketFromUrl) return;
    const mapped = BADGE_KEY_TO_BUCKET[bucketFromUrl] || bucketFromUrl;
    setActiveBucket(mapped);
  }, [bucketFromUrl]);

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/kits/today");
      setData(data);
      setError("");
      setLastRefreshed(new Date());
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Non-intrusive auto-refresh — silent (no loading spinner)
  useEffect(() => {
    if (!autoRefresh) return;
    timerRef.current = setInterval(() => load({ silent: true }), 60_000);
    return () => clearInterval(timerRef.current);
  }, [autoRefresh, load]);

  const runAction = async (path, reference, successMsg) => {
    try {
      const { data: r } = await api.post(path, { reference });
      toast.success(r.changed === false ? (r.note || "Already up to date.") : successMsg);
      load({ silent: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Structured detail on retry-blocked-live-order
      if (detail && typeof detail === "object" && detail.code === "retry_blocked_live_order_exists") {
        toast.error("Live Starshipit order exists — use the booking detail page.");
      } else {
        toast.error(formatApiError(detail) || e.message);
      }
    }
  };

  const onMarkPosted   = (ref) => runAction("/admin/kits/today/mark-posted",   ref, `Marked ${ref} posted today.`);
  const onMarkReceived = (ref) => runAction("/admin/kits/today/mark-received", ref, `Marked ${ref} received today.`);
  const onRetry        = (ref) => runAction("/admin/kits/today/retry",         ref, `Retry queued for ${ref}.`);
  const onPrint = (row) => {
    if (!row.outbound_label_url) return;
    window.open(row.outbound_label_url, "_blank", "noopener");
  };

  const rows = useMemo(() => data?.buckets?.[activeBucket] || [], [data, activeBucket]);
  const testMode = !!data?.test_mode?.starshipit_test_mode;

  return (
    <PortalShell
      title="Kits · Today"
      subtitle={`SCWT-dispatched posted kits · ${data?.today ?? ""}`}
    >
      {/* Test-mode banner — sticks at the top when Starshipit is dry-run. */}
      {testMode && (
        <div
          data-testid="kits-today-test-mode-banner"
          className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3"
        >
          <FlaskConical className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <div className="font-semibold">Starshipit is in TEST MODE (dry-run)</div>
            <div className="mt-0.5 leading-relaxed">
              Label creation and retries do NOT contact the live provider — no
              real shipments are booked. Add the API credentials in your
              environment to switch to live mode.
            </div>
          </div>
        </div>
      )}

      {/* Refresh + timestamp bar */}
      <div className="mb-5 flex items-center justify-between gap-3 text-xs">
        <div className="text-slate-500">
          {lastRefreshed && (
            <span data-testid="kits-today-last-refreshed">
              Last refreshed {lastRefreshed.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="kits-today-autorefresh-toggle"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-3.5 h-3.5 accent-cyan-600"
            />
            <span className="text-slate-500">Auto-refresh 60s</span>
          </label>
          <button
            type="button"
            data-testid="kits-today-refresh-btn"
            onClick={() => load()}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors"
          >
            <RefreshCw className="w-3 h-3" /> Refresh now
          </button>
        </div>
      </div>

      {/* Bucket cards */}
      <div data-testid="kits-today-buckets" className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {BUCKET_ORDER.map((k) => (
          <BucketCard
            key={k}
            bucketKey={k}
            rows={data?.buckets?.[k] || []}
            activeBucket={activeBucket}
            setActiveBucket={setActiveBucket}
          />
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Row list — left 2/3 */}
        <section className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              {(() => { const I = BUCKET_META[activeBucket].icon; return <I className={`w-4 h-4 ${TONE_STYLES[BUCKET_META[activeBucket].tone].icon}`} />; })()}
              {BUCKET_META[activeBucket].label}
              <span className="text-slate-400 font-normal">({rows.length})</span>
            </h2>
          </header>
          <div data-testid="kits-today-row-list">
            {loading ? (
              <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
            ) : error ? (
              <div className="p-6 text-sm text-rose-700 bg-rose-50 border-b border-rose-200">{error}</div>
            ) : rows.length === 0 ? (
              <div data-testid={`kits-today-empty-${activeBucket}`} className="p-8 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                <div>Nothing here right now.</div>
              </div>
            ) : (
              rows.map((row) => (
                <KitRow
                  key={row.reference}
                  row={row}
                  activeBucket={activeBucket}
                  onMarkPosted={onMarkPosted}
                  onMarkReceived={onMarkReceived}
                  onPrint={onPrint}
                  onRetry={onRetry}
                  onReload={load}
                />
              ))
            )}
          </div>
        </section>

        {/* Activity feed — right 1/3 */}
        <aside className="rounded-2xl border border-slate-200 bg-white p-4">
          <header className="mb-2 flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-600" />
            <h2 className="text-sm font-bold text-slate-900">Today&apos;s activity</h2>
          </header>
          <p className="text-[11px] text-slate-500 mb-3">
            Fulfilment audit-log entries from the last 24 hours.
          </p>
          <ActivityFeed items={data?.activity_feed || []} />
        </aside>
      </div>
    </PortalShell>
  );
}
